import React, { useEffect, useRef, useState } from 'react';
import { getScrollParent } from '../../AIEmployees/useReveal';
import './LinearEngineering.css';

const VIGNETTES = [
  {
    n: '01',
    title: 'Native Linear AgentSession — not webhook polling',
    body: (
      <>
        CellCog publishes to Linear&rsquo;s <code>agentActivityCreate</code>{' '}
        mutation as it works. Thoughts stream ephemerally. The final answer
        batches into a single <code>response</code> activity. When CellCog needs
        input, it emits an <code>elicitation</code> — and Linear&rsquo;s session
        state moves to <code>awaiting_input</code> automatically.
      </>
    ),
    codeBlock: (
      <>
        <span className="kw">mutation</span> <span className="fn">AgentActivityCreate</span>(
        <span className="param">$input</span>: <span className="type">AgentActivityCreateInput!</span>){' '}
        {'{'}
        <br />
        {'  '}agentActivityCreate(input: $input) {'{'}
        <br />
        {'    '}activity {'{'} id type <span className="comment">{/* thought | response | elicitation */}</span> {'}'}
        <br />
        {'  '}{'}'}
        <br />
        {'}'}
      </>
    ),
  },
  {
    n: '02',
    title: 'Two-way context via Linear\u2019s GraphQL API',
    body: (
      <>
        CellCog isn&rsquo;t frozen on the initial prompt. When it needs more
        context — sub-issues, comments, attached files, related tickets — it
        queries Linear&rsquo;s schema on-demand. Introspection-first, read-only,
        AST-validated. About half of non-trivial tasks need this, and it works
        invisibly to the user.
      </>
    ),
    codeBlock: (
      <>
        <span className="kw">query</span> <span className="fn">IssueContext</span>(
        <span className="param">$id</span>: <span className="type">String!</span>){' '}
        {'{'}
        <br />
        {'  '}issue(id: <span className="param">$id</span>) {'{'}
        <br />
        {'    '}comments(first: 50) {'{'} nodes {'{'} body user {'{'} name {'}'} {'}'} {'}'}
        <br />
        {'    '}attachments {'{'} nodes {'{'} url title {'}'} {'}'}
        <br />
        {'    '}subIssues {'{'} nodes {'{'} identifier title {'}'} {'}'}
        <br />
        {'  '}{'}'}
        <br />
        {'}'}
      </>
    ),
  },
  {
    n: '03',
    title: 'Sender identity across multi-user Linear threads',
    body: (
      <>
        One Linear workspace has many humans. We resolve who sent each specific
        message through a 6-path fallback — webhook payload first, Linear
        GraphQL as authoritative backup — so CellCog addresses each sender by
        name even when ten teammates share the same thread.
      </>
    ),
    microDetail: (
      <div className="eng-identity-card">
        <div className="eng-identity-card__label">Linear Sender (this message)</div>
        <div className="eng-identity-card__value">Sarah Chen</div>
        <div className="eng-identity-card__sub">sarah@acme.com</div>
      </div>
    ),
  },
  {
    n: '04',
    title: 'Team-based artifact access via Linear workspace membership',
    body: (
      <>
        When CellCog generates a PDF or a dashboard, your Linear workspace
        members open it with their Linear email. Sync is real-time via
        Linear&rsquo;s User webhooks — no manual sharing, no per-file
        permissions, no stale member lists.
      </>
    ),
    microDetail: (
      <div className="eng-access-card">
        <span className="eng-access-card__check">✓</span>
        <span className="eng-access-card__text">
          <strong>12 workspace members</strong> have access to generated artifacts
        </span>
      </div>
    ),
  },
  {
    n: '05',
    title: 'Secure by construction',
    body: (
      <>
        OAuth 2.0 tokens encrypted at rest with Fernet. Webhook payloads
        HMAC-verified. All user-authored content sanitized against prompt
        injection at three layers — tool responses, sender fields, attachment
        metadata. Revoke from CellCog or from Linear&rsquo;s application
        settings; we honor both.
      </>
    ),
    microDetail: (
      <div className="eng-security-card">
        <div className="eng-security-card__lock" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <div>
          <div className="eng-security-card__title">Fernet-encrypted tokens</div>
          <div className="eng-security-card__sub">RFC 7009 compliant revocation</div>
        </div>
      </div>
    ),
  },
];

const Vignette = ({ v }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { root: getScrollParent(ref.current), threshold: 0, rootMargin: '0px 0px 100% 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <div
      className={`eng-vignette reveal ${visible ? 'is-visible' : ''}`}
      ref={ref}
    >
      <div className="eng-vignette__micro">
        {v.codeBlock ? (
          <pre className="eng-vignette__code">
            <code>{v.codeBlock}</code>
          </pre>
        ) : (
          v.microDetail
        )}
      </div>
      <div className="eng-vignette__text">
        <span className="eng-vignette__num display-font">{v.n}</span>
        <h3 className="eng-vignette__title display-font">{v.title}</h3>
        <p className="eng-vignette__body">{v.body}</p>
      </div>
    </div>
  );
};

const LinearEngineering = () => (
  <section className="linear-engineering section">
    <div className="linear-engineering__header">
      <h2 className="section-title display-font">
        Built on Linear&rsquo;s agent platform —
        <br />
        not next to it.
      </h2>
      <p className="section-subtitle">
        Not a wrapper around Linear&rsquo;s API. A proper integration with
        Linear&rsquo;s actual primitives.
      </p>
    </div>

    <div className="linear-engineering__vignettes">
      {VIGNETTES.map((v) => (
        <Vignette key={v.n} v={v} />
      ))}
    </div>
  </section>
);

export default LinearEngineering;
